import React from 'react';

// Ticket Status Badge
export const TicketStatusBadge = ({ status }) => {
  const getStatusStyle = () => {
    switch (status) {
      case 'open':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'assigned':
        return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'pending_user':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'pending_vendor':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'resolved':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'closed':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatStatus = (status) => {
    if (!status) return 'Unknown';
    return status.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusStyle()}`}>
      {formatStatus(status)}
    </span>
  );
};

// Priority Badge
export const PriorityBadge = ({ priority }) => {
  const getPriorityStyle = () => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'high':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low':
        return 'bg-green-100 text-green-800 border-green-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatPriority = (priority) => {
    if (!priority) return 'Unknown';
    return priority.charAt(0).toUpperCase() + priority.slice(1);
  };

  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getPriorityStyle()}`}>
      {formatPriority(priority)}
    </span>
  );
};

// Category Badge
export const CategoryBadge = ({ category }) => {
  const getCategoryStyle = () => {
    switch (category) {
      case 'booking':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'payment':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'refund':
        return 'bg-red-50 text-red-700 border-red-200';
      case 'membership':
        return 'bg-purple-50 text-purple-700 border-purple-200';
      case 'salon_issue':
        return 'bg-orange-50 text-orange-700 border-orange-200';
      case 'app_issue':
        return 'bg-cyan-50 text-cyan-700 border-cyan-200';
      case 'complaint':
        return 'bg-pink-50 text-pink-700 border-pink-200';
      case 'general':
        return 'bg-gray-50 text-gray-700 border-gray-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const formatCategory = (category) => {
    if (!category) return 'Unknown';
    return category.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getCategoryStyle()}`}>
      {formatCategory(category)}
    </span>
  );
};

// SLA Status Indicator
export const SlaIndicator = ({ sla, status }) => {
  if (!sla || status === 'resolved' || status === 'closed') {
    return null;
  }

  const now = new Date();
  const responseDeadline = sla.responseDeadline ? new Date(sla.responseDeadline) : null;
  const resolutionDeadline = sla.resolutionDeadline ? new Date(sla.resolutionDeadline) : null;

  const isResponseBreached = responseDeadline && !sla.firstResponseAt && now > responseDeadline;
  const isResolutionBreached = resolutionDeadline && now > resolutionDeadline;

  const isAtRisk = () => {
    if (isResponseBreached || isResolutionBreached) return true;

    // At risk if within 30 minutes of deadline
    const thirtyMinutes = 30 * 60 * 1000;
    if (responseDeadline && !sla.firstResponseAt && (responseDeadline - now) < thirtyMinutes) return true;
    if (resolutionDeadline && (resolutionDeadline - now) < thirtyMinutes) return true;

    return false;
  };

  if (sla.breached || isResponseBreached || isResolutionBreached) {
    return (
      <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800 border border-red-200">
        SLA Breached
      </span>
    );
  }

  if (isAtRisk()) {
    return (
      <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800 border border-yellow-200">
        SLA At Risk
      </span>
    );
  }

  return null;
};

export default { TicketStatusBadge, PriorityBadge, CategoryBadge, SlaIndicator };
